import { useCallback, useEffect, useRef } from 'react'
import useSWR from 'swr'
import { audienceImportSessionsApi } from '@/lib/api'
import type { AudienceCreationFlowSnapshot } from '@digital-twins/shared-types'
import type { AudienceImportPreviewProjection } from '@/types/components/audiences/import-session'
import type { ImportSessionUpdatedEvent } from '@/lib/services/websocket'
import {
  useImportWizardSnapshotContext,
  useImportWizardStableContext,
} from '../context/ImportWizardContext'
import { getAudienceImportPreviewProjectionKey } from '../lib/cache-keys'
import { incrementImportSessionClientMetric } from '../lib/import-session-client-metrics'
import { useRealtimeRepair } from '@/lib/realtime/useRealtimeRepair'
import { incrementPollWonRepair } from '@/lib/realtime/pollWonMetrics'
import { legacyRefreshInterval } from '@/lib/realtime/realtimeKillSwitch'
import { shouldRevalidatePreviewProjectionForImportSessionUpdate } from '../lib/import-session-update-events'
import {
  getRouteAckFromImportSessionEvent,
  mergeRouteAckIntoPreviewProjection,
} from '../lib/import-session-deltas'

interface UsePreviewProjectionOptions {
  lastMutationRequestId?: string | null
  flowSnapshot?: AudienceCreationFlowSnapshot | null
}

function shouldPoll(
  projection: AudienceImportPreviewProjection | undefined,
  flowSnapshot?: AudienceCreationFlowSnapshot | null,
) {
  void flowSnapshot
  return projection?.preview?.previewDataPartial === true
}

export function usePreviewProjection(
  sessionId: string,
  options: UsePreviewProjectionOptions = {},
) {
  const { snapshot } = useImportWizardSnapshotContext()
  const { registerImportSessionEventHandler } = useImportWizardStableContext()
  const flowSnapshot = options.flowSnapshot ?? snapshot

  const key = getAudienceImportPreviewProjectionKey(sessionId)
  const query = useSWR<AudienceImportPreviewProjection | null>(
    key,
    sessionId
      ? (_key: unknown, options?: { signal?: AbortSignal }) =>
          audienceImportSessionsApi.getPreviewProjection(sessionId, options?.signal)
      : null,
    {
      refreshInterval: legacyRefreshInterval('IMPORT', 1500),
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    },
  )

  const revalidateProjection = useCallback(
    async (source: string) => {
      const start = performance.now()
      try {
        await query.mutate()
      } finally {
        incrementImportSessionClientMetric('preview_projection_revalidate', {
          sessionId,
          hook: 'preview',
          source,
          durationMs: Math.round(performance.now() - start),
        })
      }
    },
    [query, sessionId],
  )

  const activePoll = shouldPoll(
    query.data ?? undefined,
    flowSnapshot ?? query.data?.flowSnapshot,
  )
  useRealtimeRepair({
    active: activePoll,
    onRepair: (reason) => {
      incrementPollWonRepair('IMPORT', reason)
      void revalidateProjection(`stall_repair:${reason}`)
    },
  })

  const handlerRef = useRef<(event: ImportSessionUpdatedEvent) => void>(() => {})
  handlerRef.current = (event: ImportSessionUpdatedEvent) => {
    if (!shouldRevalidatePreviewProjectionForImportSessionUpdate(event)) {
      incrementImportSessionClientMetric('preview_projection_revalidate_skipped', {
        sessionId,
        hook: 'preview',
        source: event.reason,
      })
      return
    }
    const ack = getRouteAckFromImportSessionEvent(event)
    const needsFeaturedTwinRefresh =
      event.reason === 'preview_twin_enriched' && !ack?.previewProjection
    if (ack && !needsFeaturedTwinRefresh) {
      void query.mutate(
        (current) => {
          const merged = mergeRouteAckIntoPreviewProjection(current ?? null, ack)
          return merged ?? current
        },
        { revalidate: false },
      )
      return
    }
    void revalidateProjection(`websocket:${event.reason}`)
  }
  const stableHandler = useCallback((event: ImportSessionUpdatedEvent) => {
    handlerRef.current(event)
  }, [])

  useEffect(() => {
    return registerImportSessionEventHandler(stableHandler)
  }, [registerImportSessionEventHandler, stableHandler])

  return {
    projection: query.data ?? null,
    error: query.error as Error | undefined,
    isLoading: query.isLoading,
    isValidating: query.isValidating,
    mutate: query.mutate,
  }
}
